import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MapPin, PhoneCall, Mail, Clock3 } from 'lucide-react';

import { Button, Card, CardContent } from '@/shared/components/ui';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { SectionHeading } from '@/shared/components/SolarSections';
import { contactDetails } from '@/shared/utils/SolarData';
import { useToast } from '@/shared/contexts/ToastContext';

const contactSchema = z.object({
  name: z.string().min(2, 'Please enter your name'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(8, 'Please enter a valid phone number'),
  subject: z.string().min(3, 'Please enter a subject'),
  message: z.string().min(20, 'Please enter a message with a little more detail')
});

type ContactFormValues = z.infer<typeof contactSchema>;

const detailIcons = {
  address: MapPin,
  phone: PhoneCall,
  email: Mail,
  hours: Clock3
};

const detailLabels = {
  address: 'Address',
  phone: 'Phone',
  email: 'Email',
  hours: 'Business Hours'
};

const contactDetailEntries = Object.entries(contactDetails) as Array<
  [keyof typeof contactDetails, string]
>;

const ContactPage = () => {
  const { notify } = useToast();

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    }
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = form;

  const onSubmit = async () => {
    await new Promise(resolve => window.setTimeout(resolve, 900));
    notify('success', 'Thanks for reaching out. Our team will respond shortly.');
    reset();
  };

  return (
    <div className="px-4 pt-8 pb-16 sm:px-6 lg:px-8 lg:pt-12 lg:pb-24">
      <div className="mx-auto max-w-7xl space-y-14">
        <SectionHeading
          eyebrow="Contact Us"
          title="Start a conversation about your solar project"
          description="Share your requirements, location, and goals. We&apos;ll prepare a tailored response."
          align="center"
        />

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {contactDetailEntries.map(([title, value]) => {
            const Icon = detailIcons[title];
            return (
              <Card key={title} className="border-border-primary/70 bg-background-card/90">
                <CardContent className="p-6">
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-primary/10 text-accent-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="text-sm font-semibold uppercase tracking-[0.25em] text-text-tertiary">
                    {detailLabels[title]}
                  </p>
                  <p className="mt-2 text-sm leading-7 text-text-primary">{value}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <section className="grid gap-8 lg:grid-cols-[1fr_0.95fr] lg:items-start">
          <Card className="border-border-primary/70 bg-background-card/90">
            <CardContent className="p-6">
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-accent-primary">
                Contact form
              </p>
              <form className="mt-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Input placeholder="Full name" {...register('name')} />
                    {errors.name ? <p className="mt-2 text-sm text-accent-error">{errors.name.message}</p> : null}
                  </div>
                  <div>
                    <Input placeholder="Email address" type="email" {...register('email')} />
                    {errors.email ? <p className="mt-2 text-sm text-accent-error">{errors.email.message}</p> : null}
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Input placeholder="Phone number" {...register('phone')} />
                    {errors.phone ? <p className="mt-2 text-sm text-accent-error">{errors.phone.message}</p> : null}
                  </div>
                  <div>
                    <Input placeholder="Subject" {...register('subject')} />
                    {errors.subject ? <p className="mt-2 text-sm text-accent-error">{errors.subject.message}</p> : null}
                  </div>
                </div>

                <div>
                  <Textarea rows={7} placeholder="Tell us about your site, expected capacity, or any questions you have" {...register('message')} />
                  {errors.message ? <p className="mt-2 text-sm text-accent-error">{errors.message.message}</p> : null}
                </div>

                <Button type="submit" loading={isSubmitting} className="w-full sm:w-auto">
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="overflow-hidden border-border-primary/70 bg-background-card/90">
            <CardContent className="space-y-6 p-0">
              <div className="aspect-[4/3] bg-linear-to-br from-accent-secondary via-accent-primary to-accent-warning p-6 text-white">
                <div className="flex h-full items-center justify-center rounded-[1.5rem] border border-white/15 bg-white/10 text-center backdrop-blur-sm">
                  <div>
                    <MapPin className="mx-auto h-14 w-14" />
                    <p className="mt-4 text-2xl font-semibold">Google Map Placeholder</p>
                    <p className="mt-2 text-sm text-white/80">
                      Replace this panel with your embedded map link.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 p-6 sm:grid-cols-2">
                <div className="rounded-2xl bg-background-secondary p-4">
                  <p className="text-sm font-semibold text-text-primary">Office Location</p>
                  <p className="mt-2 text-sm leading-6 text-text-secondary">{contactDetails.address}</p>
                </div>
                <div className="rounded-2xl bg-background-secondary p-4">
                  <p className="text-sm font-semibold text-text-primary">Business Hours</p>
                  <p className="mt-2 text-sm leading-6 text-text-secondary">{contactDetails.hours}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default ContactPage;